import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '@env/environment';
import { IFlag } from '@core/interfaces';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  private readonly defaultLanguage = 'en-US';

  public readonly supportedLanguages: IFlag[] = [
    { Name: 'English', Code: 'US', Iso2: 'en', Culture: 'en-US', ShortLabel: 'EN' },
    { Name: 'German', Code: 'DE', Iso2: 'de', Culture: 'de-DE', ShortLabel: 'DE' }
  ];

  public currentLanguage: BehaviorSubject<string> = new BehaviorSubject(this.resolveInitialLanguage());

  constructor(
    private translate: TranslateService,
    private http: HttpClient,
    private authenticationService: AuthenticationService
  ) {
  }

  init(): void {
    const lang = this.resolveInitialLanguage();
    this.translate.setDefaultLang(lang);
    this.setLanguage(lang);
  }

  setLanguage(lang: string): void {
    const normalizedLanguage = this.normalizeLanguage(lang);
    this.translate.use(normalizedLanguage);
    localStorage.setItem('lang', normalizedLanguage);
    this.currentLanguage.next(normalizedLanguage);
  }

  changeLanguage(lang: string) {
    const normalizedLanguage = this.normalizeLanguage(lang);
    this.setLanguage(normalizedLanguage);
    if (this.authenticationService.currentUserValue) {
      this.authenticationService.updateCurrentUser({ culture: normalizedLanguage });
    }
    return this.http.get(`${environment.apiUrl}api/Globalization/SetCulture/${normalizedLanguage}`);
  }

  syncWithUserLanguage(lang?: string | null): void {
    if (!lang) {
      return;
    }

    const normalizedLanguage = this.normalizeLanguage(lang);
    if (this.currentLanguage.getValue() !== normalizedLanguage) {
      this.setLanguage(normalizedLanguage);
    }
  }

  getLanguageByCulture(culture?: string | null): IFlag | undefined {
    return this.supportedLanguages.find(item => item.Culture === this.normalizeLanguage(culture));
  }

  private resolveInitialLanguage(): string {
    const accountLanguage = this.authenticationService.currentUserValue?.culture;
    const storedLanguage = localStorage.getItem('lang');
    return this.normalizeLanguage(accountLanguage || storedLanguage || this.defaultLanguage);
  }

  /**
   * Maps any stored culture value to a supported culture code. Accepts the full
   * culture ("de-DE"), a short ISO code ("de") or a regional variant ("de-AT"),
   * in any casing, and falls back to the default language otherwise.
   */
  normalizeLanguage(lang?: string | null): string {
    const value = (lang || '').trim().toLowerCase();
    const iso2 = value.split(/[-_]/)[0];
    const selectedLanguage =
      this.supportedLanguages.find(item => item.Culture.toLowerCase() === value) ||
      this.supportedLanguages.find(item => item.Iso2 === iso2);
    return selectedLanguage?.Culture || this.defaultLanguage;
  }

}
